import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, ArrowRight, Users, Heart, Shield, Target } from 'lucide-react';
import { companyInfo } from '../data/mock';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Confiance',
      description: 'Nous bâtissons des relations durables basées sur la transparence et l\'honnêteté. Chaque transaction est sécurisée juridiquement.'
    },
    {
      icon: Target,
      title: 'Excellence',
      description: 'Nous visons l\'excellence dans chaque projet, de la sélection des biens à la livraison finale. La qualité est notre priorité.'
    },
    {
      icon: Users,
      title: 'Accompagnement',
      description: 'Nos équipes vous accompagnent à chaque étape de votre projet. Votre satisfaction est notre réussite.'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'L\'immobilier est notre passion. Nous mettons tout notre cœur pour transformer vos rêves en réalité.'
    }
  ];

  const commitments = [
    'Sécurité juridique garantie sur toutes les transactions',
    'Expertise locale approfondie du marché sénégalais',
    'Accompagnement personnalisé de A à Z',
    'Transparence totale sur les prix et les procédures',
    'Réactivité et disponibilité de nos équipes',
    'Suivi post-vente et service après-vente'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('https://images.unsplash.com/photo-1613977257592-4871e5fcd7c4')`
          }}
        />
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">À propos de nous</h1>
          <p className="text-xl max-w-2xl mx-auto text-slate-200">
            Votre partenaire de confiance pour tous vos projets immobiliers au Sénégal
          </p>
        </div>
      </section>

      {/* Company Presentation */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Qui sommes-nous ?</h2>
              <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                <p>
                  <span className="font-bold text-amber-600">{companyInfo.name}</span> est une entreprise immobilière de référence au Sénégal, 
                  spécialisée dans la vente de terrains, la vente de biens immobiliers, la construction clé en main, 
                  la gérance immobilière, le terrassement et le lotissement.
                </p>
                <p>
                  Forte de plusieurs années d'expérience dans le secteur immobilier sénégalais, notre entreprise s'est 
                  imposée comme un acteur majeur grâce à son expertise locale, son professionnalisme et son engagement 
                  sans faille envers la satisfaction de ses clients.
                </p>
                <p>
                  Nous accompagnons aussi bien les particuliers, les investisseurs immobiliers, les entreprises que 
                  la diaspora dans la réalisation de leurs projets immobiliers. Chaque projet est unique et mérite 
                  une attention particulière, c'est pourquoi nous mettons tout en œuvre pour offrir un service personnalisé 
                  et de qualité.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="border-2 border-amber-100">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-6">
                    <Target className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Notre Vision</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Devenir la référence incontournable de l'immobilier au Sénégal en offrant des services d'excellence 
                    et en contribuant au développement d'un secteur immobilier moderne, transparent et accessible à tous.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-amber-100">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-6">
                    <Heart className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Notre Mission</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Faciliter l'accès à la propriété et accompagner nos clients dans la réalisation de leurs projets 
                    immobiliers avec professionnalisme, transparence et sécurité juridique garantie.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Nos Valeurs</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Les principes qui guident notre action au quotidien
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center group hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6 group-hover:bg-amber-600 transition-colors">
                    <value.icon className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Notre Engagement Qualité</h2>
              <p className="text-lg text-slate-600">
                Chez PAM IMMO SERVICES, nous nous engageons à vous offrir le meilleur service
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {commitments.map((commitment, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-amber-600" />
                  </div>
                  <p className="text-slate-700 font-medium">{commitment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Expertise Locale</h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Notre connaissance approfondie du marché immobilier sénégalais, combinée à notre réseau étendu 
              de partenaires locaux, nous permet de vous proposer les meilleures opportunités et de vous garantir 
              un accompagnement de qualité sur l'ensemble du territoire.
            </p>
            <p className="text-lg text-slate-300 mb-12">
              De Dakar à Saly, en passant par Diamniadio et toutes les régions du Sénégal, nous connaissons 
              chaque quartier, chaque zone, et pouvons vous conseiller efficacement pour faire le meilleur choix.
            </p>
            <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700 text-lg px-8">
              <Link to="/contact">
                Discutons de votre projet
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;